import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCenter } from 'ol/extent';
import Static from 'ol/source/ImageStatic';
import { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MdEditSquare } from 'react-icons/md';
import { TbDownload } from 'react-icons/tb';
import { RiDeleteBin6Fill } from 'react-icons/ri';
import Loader from '../../../components/CoreComponents/Loader';
import Toggle from '../../../components/CoreComponents/Toggle';
import ViewTable from '../../../components/CoreComponents/ViewTable';
import { fextent, layerContext } from '../../../components/Providers/LayersProvider';
import { URLs } from '../../../Constants/Consts';
import { fetchApi } from '../../../Helpers/Functions';
import { FileDrawingType } from '../../../types/types';
import { RootState } from '../../../store/store';
import { updateDownloadTriggered } from '../../../store/Utilities';
import roleCheck from '../../../utils/roleCheck';
import { ROLES } from '../../../utils/roles';

const ModalModes = {
    view: "view",
    add: "add",
    update: "update",
}
const ProjectMapsQueryKey = "ProjectMaps"

interface FileDrawingTypeExtended extends FileDrawingType {
    loadForProject?: boolean;
}

export default function ProjectMaps() {
    const { projectId } = useParams()
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    
    // Role checking
    const { role: userRole } = useSelector((state: RootState) => state.SelectedUser.SelectedUser);
    const isSuperAdmin = roleCheck(userRole, [ROLES.SuperAdmin]);

    // Load-for-project toggle state
    const [updatingDrawing, setUpdatingDrawing] = useState<string | null>(null);
    const [deletingDrawing, setDeletingDrawing] = useState<string | null>(null);
    const [countdown, setCountdown] = useState<number | null>(null);
    const [pendingReload, setPendingReload] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Countdown timer effect
    useEffect(() => {
        if (countdown === null) return;

        if (countdown === 0) {
            // Trigger Redux store refresh
            dispatch(updateDownloadTriggered({ key: "drawings", value: true }));
            setCountdown(null);
            setPendingReload(false);
            return;
        }

        timerRef.current = setTimeout(() => {
            setCountdown(countdown - 1);
        }, 1000);

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [countdown, dispatch]);
    
    // view -get
    const { data: projectMaps, isLoading } = useQuery<Array<{
        url: string;
        label: string;
        id: string;
        loadForProject: boolean;
        filename: string;
    }>>({
        queryKey: [ProjectMapsQueryKey],
        queryFn: async () => {
            const drawings = await fetchApi(URLs.getDrawingsUrl(Number(projectId))).then(res => res.json()) as Array<FileDrawingTypeExtended>;
            const drawingsData = await Promise.all(drawings.map(async ({ projectNumber, drawingFilename, drawingDisplayName, drawingUuid, loadForProject = false }) => {
                const fileblob = await (await fetchApi(URLs.getFileBlobUrl2(Number(projectNumber), String(drawingFilename)))).blob()

                return {
                    url: URL.createObjectURL(fileblob),
                    label: drawingDisplayName as string,
                    id: String(drawingUuid),
                    loadForProject: loadForProject,
                    filename: drawingFilename as string,
                }
            }))
            return drawingsData;
        },
        staleTime: 0
    });

    const toggleLoadForProject = async (drawingUuid: string, currentValue: boolean, drawingFilename: string, drawingDisplayName: string) => {
        setUpdatingDrawing(drawingUuid);

        try {
            await fetchApi(URLs.putDrawingsUrl(drawingUuid), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    drawingUuid: drawingUuid,
                    drawingFilename: drawingFilename,
                    drawingDisplayName: drawingDisplayName,
                    projectNumber: Number(projectId),
                    loadForProject: !currentValue,
                }),
            });

            await queryClient.invalidateQueries({ queryKey: [ProjectMapsQueryKey] });

            // Start/reset the countdown timer for Redux store refresh
            setPendingReload(true);
            setCountdown(5);
        } catch (error) {
            console.error("Error updating load_for_project:", error);
        } finally {
            setUpdatingDrawing(null);
        }
    };

    const downloadMap = (url: string, filename: string) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || 'map';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const deleteMap = async (drawingUuid: string, drawingLabel: string) => {
        const confirmed = window.confirm(`Are you sure you want to delete "${drawingLabel}"? This action cannot be undone.`);
        if (!confirmed) return;

        setDeletingDrawing(drawingUuid);
        try {
            const res = await fetchApi(URLs.deleteDrawingUrl(drawingUuid), {
                method: 'DELETE',
            });
            if (!res.ok) {
                throw new Error('Failed to delete drawing');
            }
            await queryClient.invalidateQueries({ queryKey: [ProjectMapsQueryKey] });

            // Notify other parts of the app that drawings changed
            setPendingReload(true);
            setCountdown(5);
        } catch (error) {
            console.error("Error deleting drawing:", error);
        } finally {
            setDeletingDrawing(null);
        }
    };
    // update
    // add 
    const [modalMode, setModalMode] = useState(ModalModes.view)
    const [openModal, setOpenModal] = useState(false)
    const [updatedDrawingUuid, setUpdatedDrawingUuid] = useState("")
    const { mapObject, staticImageLayer } = useContext(layerContext)
    const mapRef = useRef<HTMLDivElement>()
    const [selectedMap, setselectedMap] = useState(undefined)
    useEffect(() => {
        if (mapObject) {
            mapObject.setTarget(mapRef.current)
            mapObject.getView()?.setZoom(2);
            mapObject.getView()?.setCenter(getCenter(fextent));
        }
        if (staticImageLayer && selectedMap) staticImageLayer.setSource(new Static({
            url: selectedMap,
            projection: "EPSG:4326",
            imageExtent: fextent,
        }),)
    }, [mapObject, staticImageLayer, selectedMap])

    return (
        <>
            {isLoading ? <Loader loaders={{ isProjectMapsLoading: true }} /> :
                <div className='font-bold'>
                    <div className='flex justify-between items-center mb-4'>
                        <span > Project Number: {projectId}</span>
                        {isSuperAdmin && (
                            <button
                                className="bg-[#fdb419] text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline shadow-md transition-colors duration-200" // Tailwind classes
                                onClick={() => {
                                    setModalMode(ModalModes.add);
                                    setOpenModal(true)
                                }}
                            >Add New Map</button>
                        )}
                    </div>
                    {pendingReload && countdown !== null && (
                        <div className='mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg'>
                            <p className='text-sm font-normal text-blue-800 dark:text-blue-200'>
                                Map selections will sync in <span className='font-bold'>{countdown}</span> second{countdown !== 1 ? 's' : ''}...
                            </p>
                        </div>
                    )}
                    <ViewTable
                        columns={[
                            {
                                Header: 'Drawing Name',
                                accessor: 'label',
                            },
                            {
                                Header: 'Map',
                                accessor: 'url',
                                Cell: ({ value }) => (
                                    <img onClick={() => {
                                        setselectedMap(value);
                                        setModalMode(ModalModes.view);
                                        setOpenModal(true)
                                    }}
                                        title='View Map' src={value} style={{ width: "50px" }} />
                                )
                            },
                            {
                                Header: 'Load for Project',
                                accessor: 'loadForProject',
                                Cell: ({ row }) => {
                                    const isUpdating = updatingDrawing === row.original.id;
                                    const isLoaded = row.original.loadForProject;

                                    return (
                                        <div className="flex items-center gap-3">
                                            <span className={`text-sm font-medium min-w-[90px] ${isLoaded ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
                                                {isUpdating ? 'Updating...' : isLoaded ? 'Loaded' : 'Not Loaded'}
                                            </span>
                                            <Toggle
                                                checked={isLoaded}
                                                disabled={isUpdating}
                                                onChange={() => toggleLoadForProject(
                                                    row.original.id,
                                                    isLoaded,
                                                    row.original.filename,
                                                    row.original.label
                                                )}
                                                ariaLabel={`Toggle load for project: ${row.original.label}`}
                                            />
                                        </div>
                                    );
                                }
                            },
                            ...(isSuperAdmin ? [{
                                Header: 'Actions',
                                Cell: (tableRow) => {
                                    const drawingUuid = tableRow.row.original.id;
                                    const drawingUrl = tableRow.row.original.url;
                                    const drawingLabel = tableRow.row.original.label;
                                    const drawingFilename = tableRow.row.original.filename;
                                    const isDeleting = deletingDrawing === drawingUuid;

                                    return (
                                        <div className="flex items-center gap-4">
                                            <button
                                                onClick={() => {
                                                    setModalMode(ModalModes.update);
                                                    setOpenModal(true)
                                                    setUpdatedDrawingUuid(drawingUuid);
                                                }}
                                                title='Edit Map'
                                                aria-label={`Edit map: ${drawingLabel}`}
                                                className='bg-orange-1000 hover:bg-gray-800 text-white p-3 rounded focus:outline-none focus:shadow-outline shadow-md transition-colors duration-200 inline-flex items-center justify-center'
                                            >
                                                <MdEditSquare className='w-5 h-5' />
                                            </button>
                                            <button
                                                onClick={() => downloadMap(drawingUrl, drawingFilename || drawingLabel)}
                                                title='Download Map'
                                                aria-label={`Download map: ${drawingLabel}`}
                                                className='bg-blue-600 hover:bg-blue-800 text-white p-3 rounded focus:outline-none focus:shadow-outline shadow-md transition-colors duration-200 inline-flex items-center justify-center'
                                            >
                                                <TbDownload className='w-5 h-5' />
                                            </button>
                                            <button
                                                onClick={() => deleteMap(drawingUuid, drawingLabel)}
                                                disabled={isDeleting}
                                                title='Delete Map'
                                                aria-label={`Delete map: ${drawingLabel}`}
                                                className='bg-red-600 hover:bg-red-800 text-white p-3 rounded focus:outline-none focus:shadow-outline shadow-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center'
                                            >
                                                <RiDeleteBin6Fill className='w-5 h-5' />
                                            </button>
                                        </div>
                                    )
                                }
                            }] : [])
                        ]}
                        data={projectMaps}
                    />
                    {isSuperAdmin && (
                        <div className='mt-6 p-4 bg-gray-100 dark:bg-gray-1000 border-2 border-uniteOrange rounded-lg'>
                            <p className='text-sm font-bold mb-2 text-black dark:text-uniteOrange'>
                                Important: How map changes affect Punch Lists
                            </p>
                            <ul className='list-disc list-inside text-sm font-normal text-black dark:text-white space-y-1'>
                                <li>
                                    <span className='font-semibold text-black dark:text-uniteOrange'>Renaming</span> a map's display name or file name will <span className='font-semibold text-black dark:text-uniteOrange'>not</span> break existing Punch Lists. Punch List locations are tied to the map's UUID, not its name.
                                </li>
                                <li>
                                    <span className='font-semibold text-black dark:text-uniteOrange'>Unloading</span> a map (toggling "Load for Project" off) only hides it from users in the field; all existing Punch List locations are preserved and will work again once the map is reloaded.
                                </li>
                                <li>
                                    <span className='font-semibold text-black dark:text-uniteOrange'>Deleting</span> a map that has Punch Lists associated with it will remove the ability to view those Punch List locations on a map.
                                </li>
                                <li>
                                    <span className='font-semibold text-black dark:text-uniteOrange'>Deleting and re-uploading</span> the same map (even with an identical display name and file name) generates a new UUID, which will break the location link for any related Punch Lists.
                                </li>
                            </ul>
                        </div>
                    )}
                </div>}
            <MapsModal
                onClose={() => {
                    setOpenModal(false);
                    setselectedMap(undefined)
                }}
                mode={modalMode}
                open={openModal}
                selectedMap={selectedMap}
                setselectedMap={setselectedMap}
                mapRef={mapRef}
                drawingUuid={updatedDrawingUuid}
                existingDrawings={projectMaps || []}
            />
        </>
    )
}


const MapsModal = ({ mode, drawingUuid, selectedMap, open, mapRef, setselectedMap, onClose, existingDrawings }:
    {
        mode: string,
        drawingUuid: string,
        selectedMap: string,
        open: boolean,
        mapRef: React.MutableRefObject<HTMLDivElement>,
        setselectedMap: (blobUrl) => void
        onClose: () => void,
        existingDrawings: Array<{ id: string; filename: string; label: string; loadForProject: boolean }>,
    }) => {

    const { projectId } = useParams()
    const queryClient = useQueryClient()
    const [isLoading, setIsLoading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 })
    const fileInputRef = useRef(null);
    const [newDrawingName, setNewDrawingName] = useState("")
    const [newDrawingFilename, setNewDrawingFilename] = useState("")
    const [newMap, setNewMap] = useState<File>(undefined)
    const [selectedFiles, setSelectedFiles] = useState<Array<{ file: File, name: string, id: string }>>([])
    const [fileError, setFileError] = useState<string>("")
    const [uploadError, setUploadError] = useState<string>("")

    // Filenames already present on the project (case-insensitive lookup).
    // In update mode we exclude the drawing being edited so re-saving the same file is allowed.
    const existingFilenamesLower = existingDrawings
        .filter(d => mode !== ModalModes.update || d.id !== drawingUuid)
        .map(d => (d.filename || '').toLowerCase());

    const isDuplicateFilename = (filename: string): boolean => {
        if (!filename) return false;
        return existingFilenamesLower.includes(filename.toLowerCase());
    }

    // For the update flow, surface a conflict if the typed filename collides with another drawing.
    const updateModeConflict = mode === ModalModes.update && !!newDrawingFilename && isDuplicateFilename(newDrawingFilename);

    // Seed the drawing-name and filename inputs with the existing values when the edit modal opens.
    useEffect(() => {
        if (!open) return;
        if (mode === ModalModes.update) {
            const existing = existingDrawings.find(d => d.id === drawingUuid);
            if (existing) {
                setNewDrawingName(existing.label || '');
                setNewDrawingFilename(existing.filename || '');
            }
        }
        // We intentionally only seed when the modal opens or the target drawing changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, mode, drawingUuid]);

    const isValidImageFile = (file: File): boolean => {
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        return validTypes.includes(file.type.toLowerCase());
    }

    const filterValidImages = (files: File[]): { valid: File[], invalid: File[] } => {
        const valid = files.filter(isValidImageFile);
        const invalid = files.filter(f => !isValidImageFile(f));
        return { valid, invalid };
    }

    const uploadSingleMap = async (file: File, displayName: string, uuid?: string, filenameOverride?: string) => {
        const uploadUrl = URLs.postDrawingsUrl2()
        const formData = new FormData();
        
        formData.append("files", file);
        formData.append("projectNumber", String(projectId));
        formData.append("overwriteYN", 'true');
        
        await fetchApi(uploadUrl, {
            method: "POST",
            body: formData
        }).then(async (res) => {
            if (!res.ok) {
                throw new Error("Failed to upload file");
            }
            await res.json();
        }).catch((error) => {
            console.error("Error uploading file:", error);
            throw error;
        });
        
        let attachDrawingUrl, method, body;
        if (mode === ModalModes.update && uuid) {
            const existing = existingDrawings.find(d => d.id === uuid);
            attachDrawingUrl = URLs.putDrawingsUrl(uuid)
            method = "PUT"
            body = {
                drawingFilename: filenameOverride || file.name,
                "drawingUuid": uuid,
                "drawingDisplayName": displayName,
                "projectNumber": Number(projectId),
                "loadForProject": existing?.loadForProject ?? false,
            }
        } else {
            attachDrawingUrl = URLs.postDrawingsUrl()
            method = "POST"
            body = {
                "drawingUuid": crypto.randomUUID().toUpperCase(),
                "drawingDisplayName": displayName,
                drawingFilename: file.name,
                "projectNumber": Number(projectId),
            }
        }
        
        await fetchApi(attachDrawingUrl, {
            method,
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body),
        })
    }

    const updateDrawingMetadata = async (uuid: string, displayName: string, filename: string) => {
        const existing = existingDrawings.find(d => d.id === uuid);
        const res = await fetchApi(URLs.putDrawingsUrl(uuid), {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                drawingUuid: uuid,
                drawingFilename: filename,
                drawingDisplayName: displayName,
                projectNumber: Number(projectId),
                loadForProject: existing?.loadForProject ?? false,
            }),
        });
        if (!res.ok) {
            throw new Error('Failed to update drawing metadata');
        }
    }

    const attachMapToProject = async () => {
        // Block upload when any selected file collides with an existing drawing filename.
        if (mode === ModalModes.update) {
            const targetFilename = newMap ? newMap.name : newDrawingFilename;
            if (!targetFilename.trim()) {
                setUploadError('File name cannot be empty.');
                return;
            }
            if (isDuplicateFilename(targetFilename)) {
                setUploadError(`A map with the filename "${targetFilename}" already exists on this project. Please choose a different file name.`);
                return;
            }
        } else {
            const filesToCheck = selectedFiles.length > 0
                ? selectedFiles
                : (newMap ? [{ file: newMap, name: newDrawingName, id: '' }] : []);
            const conflicts = filesToCheck.filter(f => isDuplicateFilename(f.file.name));
            if (conflicts.length > 0) {
                const names = conflicts.map(f => `"${f.file.name}"`).join(', ');
                setUploadError(`Cannot upload. The following file${conflicts.length !== 1 ? 's' : ''} already exist${conflicts.length !== 1 ? '' : 's'} on this project: ${names}. Please remove or rename ${conflicts.length !== 1 ? 'them' : 'it'} before uploading.`);
                return;
            }
        }

        setIsLoading(true)
        
        try {
            if (mode === ModalModes.update) {
                // Single file update for edit mode
                if (newMap) {
                    // A new image was provided; upload it and PUT metadata using the typed filename.
                    await uploadSingleMap(newMap, newDrawingName, drawingUuid, newDrawingFilename || newMap.name);
                } else {
                    // Metadata-only update: just PUT the display name / filename / loadForProject.
                    await updateDrawingMetadata(drawingUuid, newDrawingName, newDrawingFilename);
                }
            } else {
                // Multiple files for add mode
                const filesToUpload = selectedFiles.length > 0 ? selectedFiles : (newMap ? [{ file: newMap, name: newDrawingName, id: '' }] : []);
                setUploadProgress({ current: 0, total: filesToUpload.length });
                
                for (let i = 0; i < filesToUpload.length; i++) {
                    setUploadProgress({ current: i + 1, total: filesToUpload.length });
                    await uploadSingleMap(filesToUpload[i].file, filesToUpload[i].name);
                }
            }
            
            await queryClient.invalidateQueries({ queryKey: [ProjectMapsQueryKey] })
            setIsLoading(false)
            onModalClose()
        } catch (error) {
            console.error("Error during upload:", error);
            setIsLoading(false)
        }
    }
    const onModalClose = () => {
        onClose()
        setNewMap(undefined);
        setNewDrawingName("");
        setNewDrawingFilename("");
        setSelectedFiles([]);
        setUploadProgress({ current: 0, total: 0 });
        setFileError("");
        setUploadError("");
    }
    if (!open) return <></>;
    return (
        <button
            onClick={onModalClose}
            className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">

            <button
                onClick={(e) => {
                    e.stopPropagation();
                }}
                className="bg-white dark:bg-gray-1000 rounded-lg shadow-lg p-6 w-max max-h-[90vh] flex flex-col">
                {isLoading ? (
                    <div className='flex flex-col items-center gap-4'>
                        <Loader loaders={{ isMapsUpdatingLoading: true }} />
                        {uploadProgress.total > 0 && (
                            <div className='text-center'>
                                <p className='font-semibold text-gray-700 dark:text-gray-200'>
                                    Uploading {uploadProgress.current} of {uploadProgress.total}...
                                </p>
                            </div>
                        )}
                    </div>
                ) :
                    <>
                        {mode !== ModalModes.view && (
                            <div className='flex flex-col gap-6 min-w-[500px]'>
                                {/* Upload Section */}
                                <div className='flex flex-col gap-2'>
                                    <label className='font-semibold text-gray-700 dark:text-gray-200'>
                                        {mode === ModalModes.update ? 'Map Image' : 'Map Images'}
                                    </label>
                                    <input 
                                        ref={fileInputRef} 
                                        type="file" 
                                        style={{ display: "none" }}
                                        accept="image/jpeg, image/png"
                                        multiple={mode !== ModalModes.update}
                                        onChange={e => {
                                            const files = Array.from(e.target.files || []);
                                            const { valid, invalid } = filterValidImages(files);
                                            
                                            if (invalid.length > 0) {
                                                setFileError(`File format not supported: ${invalid.map(f => f.name).join(', ')}. Only JPEG and PNG files are allowed.`);
                                                setTimeout(() => setFileError(""), 5000);
                                            }
                                            
                                            if (valid.length > 0) {
                                                if (mode === ModalModes.update) {
                                                    const file = valid[0];
                                                    setNewMap(file);
                                                    if (!newDrawingName) setNewDrawingName(file.name);
                                                    setNewDrawingFilename(file.name);
                                                    setselectedMap(URL.createObjectURL(file));
                                                } else {
                                                    const newFiles = valid.map(file => ({
                                                        file,
                                                        name: file.name,
                                                        id: crypto.randomUUID()
                                                    }));
                                                    setSelectedFiles(prev => [...prev, ...newFiles]);
                                                }
                                            }
                                            
                                            // Reset the input
                                            e.target.value = '';
                                        }} 
                                    />
                                    <div 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            fileInputRef.current.click();
                                        }}
                                        className='border-2 border-dashed border-slate-300 dark:border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-orange-500 hover:bg-orange-50 dark:hover:bg-gray-700 transition-colors'
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const files = Array.from(e.dataTransfer.files);
                                            const { valid, invalid } = filterValidImages(files);
                                            
                                            if (invalid.length > 0) {
                                                setFileError(`File format not supported: ${invalid.map(f => f.name).join(', ')}. Only JPEG and PNG files are allowed.`);
                                                setTimeout(() => setFileError(""), 5000);
                                            }
                                            
                                            if (valid.length > 0) {
                                                if (mode === ModalModes.update) {
                                                    const file = valid[0];
                                                    setNewMap(file);
                                                    if (!newDrawingName) setNewDrawingName(file.name);
                                                    setNewDrawingFilename(file.name);
                                                    setselectedMap(URL.createObjectURL(file));
                                                } else {
                                                    const newFiles = valid.map(file => ({
                                                        file,
                                                        name: file.name,
                                                        id: crypto.randomUUID()
                                                    }));
                                                    setSelectedFiles(prev => [...prev, ...newFiles]);
                                                }
                                            }
                                        }}
                                    >
                                        {mode === ModalModes.update && newMap ? (
                                            <div className='flex items-center justify-center gap-2 text-green-600 dark:text-green-400'>
                                                <span className='text-lg'>✓</span>
                                                <span className='font-medium'>{newMap.name}</span>
                                            </div>
                                        ) : (
                                            <div className='flex flex-col items-center gap-2 text-gray-500 dark:text-gray-400'>
                                                <span className='text-3xl'>📁</span>
                                                <span className='font-medium'>
                                                    {mode === ModalModes.update 
                                                        ? 'Click or drag to select an image' 
                                                        : 'Click or drag to select images'}
                                                </span>
                                                <span className='text-sm text-gray-400 dark:text-gray-500'>JPEG and PNG only</span>
                                            </div>
                                        )}
                                    </div>
                                    {fileError && (
                                        <div className='text-red-600 dark:text-red-400 text-sm p-3 bg-red-50 dark:bg-red-900/20 rounded-md border border-red-200 dark:border-red-800'>
                                            {fileError}
                                        </div>
                                    )}
                                    {updateModeConflict && (
                                        <div className='text-red-600 dark:text-red-400 text-sm p-3 bg-red-50 dark:bg-red-900/20 rounded-md border border-red-200 dark:border-red-800'>
                                            A map with the filename "{newDrawingFilename}" already exists on this project. Please choose a different file name.
                                        </div>
                                    )}
                                </div>

                                {/* Drawing Name Field (for single update mode) */}
                                {mode === ModalModes.update && (
                                    <div className='flex flex-col gap-3'>
                                        <div className='flex items-center justify-between'>
                                            <label htmlFor="newDrawingName" className='font-semibold text-gray-700 dark:text-gray-200'>Drawing Name</label>
                                            <input 
                                                type="text"
                                                id='newDrawingName'
                                                value={newDrawingName}
                                                className='border-2 border-slate-300 dark:border-gray-600 rounded-md p-2 w-64 focus:border-orange-500 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                                                placeholder='Enter drawing name'
                                                onChange={e => setNewDrawingName(e.target.value)}
                                            />
                                        </div>
                                        <div className='flex items-center justify-between'>
                                            <label htmlFor="newDrawingFilename" className='font-semibold text-gray-700 dark:text-gray-200'>File Name</label>
                                            <input 
                                                type="text"
                                                id='newDrawingFilename'
                                                value={newDrawingFilename}
                                                className={`border-2 rounded-md p-2 w-64 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${updateModeConflict ? 'border-red-500 focus:border-red-600' : 'border-slate-300 dark:border-gray-600 focus:border-orange-500'}`}
                                                placeholder='Enter file name'
                                                onChange={e => setNewDrawingFilename(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Selected Files List (for add mode) */}
                                {mode !== ModalModes.update && selectedFiles.length > 0 && (
                                    <div className='flex flex-col gap-2'>
                                        <div className='flex items-center justify-between'>
                                            <label className='font-semibold text-gray-700 dark:text-gray-200'>
                                                Selected Maps ({selectedFiles.length})
                                            </label>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedFiles([]);
                                                }}
                                                className='text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300'
                                            >
                                                Clear All
                                            </button>
                                        </div>
                                        <div className='max-h-[300px] overflow-y-auto border border-slate-300 dark:border-gray-600 rounded-md'>
                                            {selectedFiles.map((fileItem, index) => {
                                                const isDuplicate = isDuplicateFilename(fileItem.file.name);
                                                return (
                                                <div 
                                                    key={fileItem.id} 
                                                    className='flex flex-col gap-1 p-3 border-b last:border-b-0 border-slate-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <div className='flex items-center gap-3'>
                                                    <span className='text-sm text-gray-500 dark:text-gray-400 min-w-[30px]'>
                                                        {index + 1}.
                                                    </span>
                                                    <img 
                                                        src={URL.createObjectURL(fileItem.file)} 
                                                        alt={fileItem.name}
                                                        className='w-12 h-12 object-cover rounded border border-slate-300 dark:border-gray-600'
                                                    />
                                                    <input 
                                                        type="text"
                                                        value={fileItem.name}
                                                        className={`flex-1 border rounded p-2 text-sm focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${isDuplicate ? 'border-red-500 focus:border-red-600' : 'border-slate-300 dark:border-gray-600 focus:border-orange-500'}`}
                                                        placeholder='Enter drawing name'
                                                        onChange={e => {
                                                            setSelectedFiles(prev => 
                                                                prev.map(f => 
                                                                    f.id === fileItem.id 
                                                                        ? { ...f, name: e.target.value } 
                                                                        : f
                                                                )
                                                            );
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedFiles(prev => 
                                                                prev.filter(f => f.id !== fileItem.id)
                                                            );
                                                        }}
                                                        className='text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 px-2'
                                                        title='Remove'
                                                    >
                                                        ✕
                                                    </button>
                                                    </div>
                                                    {isDuplicate && (
                                                        <div className='text-xs text-red-600 dark:text-red-400 pl-[105px]'>
                                                            A map with the filename "{fileItem.file.name}" already exists on this project.
                                                        </div>
                                                    )}
                                                </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div className='flex justify-between pt-4 border-t border-gray-200 dark:border-gray-600'>
                                    <button 
                                        onClick={onModalClose}
                                        className='px-6 py-2 rounded font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors'
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        onClick={attachMapToProject}
                                        disabled={mode === ModalModes.update ? (!newDrawingName.trim() || !newDrawingFilename.trim()) : selectedFiles.length === 0}
                                        className='px-6 py-2 rounded font-medium text-white bg-orange-1000 hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                                    >
                                        {mode === ModalModes.update ? 'Save' : `Upload ${selectedFiles.length} Map${selectedFiles.length !== 1 ? 's' : ''}`}
                                    </button>
                                </div>
                            </div>
                        )}
                        {mode === ModalModes.view && (
                            <button
                                onClick={onModalClose}
                                className="absolute top-2 right-2 text-slate-50 hover:text-red-500 scale-150 hover:scale-250"
                            >
                                &times;
                            </button>
                        )}
                        {(newMap || selectedMap) && mode === ModalModes.view && <div className="w-[85vw] h-[85svh] relative" ref={mapRef}></div>}
                    </>}
                {uploadError && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setUploadError("");
                        }}
                        className='fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-[60]'
                    >
                        <button
                            onClick={(e) => e.stopPropagation()}
                            className='bg-white dark:bg-gray-1000 rounded-lg shadow-xl p-6 max-w-md mx-4 flex flex-col gap-4 text-left'
                        >
                            <h3 className='text-lg font-bold text-red-600 dark:text-red-400'>Upload Blocked</h3>
                            <p className='text-sm font-normal text-gray-700 dark:text-gray-200'>{uploadError}</p>
                            <div className='flex justify-end'>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setUploadError("");
                                    }}
                                    className='px-4 py-2 rounded font-medium text-white bg-orange-1000 hover:bg-gray-800 transition-colors'
                                >
                                    OK
                                </button>
                            </div>
                        </button>
                    </button>
                )}
            </button>
        </button>
    );
}